import { ReactNode, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DesktopSidebar } from "@/components/navigation/DesktopSidebar";
import MobileBottomNav from "@/components/navigation/MobileBottomNav";
import { DocumentDragProvider } from "@/contexts/DocumentDragContext";
import { cn } from "@/lib/utils";

interface AppLayoutProps {
  children: ReactNode;
}

// Pages where we don't show the app shell (sidebar/bottom nav)
const PUBLIC_PATHS = [
  "/",
  "/auth",
  "/onboarding",
  "/pricing",
  "/privacy",
  "/privacy-policy",
  "/privacy-policy-en",
  "/terms",
  "/legal",
  "/reset-password",
  "/security-faq",
  "/blog",
  "/incident-response-policy",
  "/incident-response-policy-en",
  "/vulnerability-disclosure-policy",
  "/vulnerability-disclosure-policy-en",
  "/information-security-policy",
  "/information-security-policy-en",
];

const isPublicPath = (pathname: string) => {
  if (PUBLIC_PATHS.includes(pathname)) return true;
  if (pathname.startsWith("/share/")) return true;
  if (pathname.startsWith("/collection/")) return true;
  if (pathname.startsWith("/portal/")) return true;
  if (pathname.startsWith("/blog/")) return true;
  return false;
};

// Pages where we show full-width content (no sidebar inset)
const FULL_WIDTH_PATHS = [
  "/documents/",
  "/onboarding",
];

const isFullWidthPath = (pathname: string) => {
  return FULL_WIDTH_PATHS.some(path => pathname.startsWith(path));
};

export const AppLayout = ({ children }: AppLayoutProps) => {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("sidebar-collapsed") === "true";
    }
    return false;
  });

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", sidebarCollapsed ? "true" : "false");
  }, [sidebarCollapsed]);

  const isPublic = isPublicPath(location.pathname);
  const isFullWidth = isFullWidthPath(location.pathname);

  // Public pages: render children without app shell
  if (isPublic) {
    return <>{children}</>;
  }

  return (
    <DocumentDragProvider>
      <div className="min-h-screen flex w-full max-w-full overflow-x-hidden bg-background">
        {/* Desktop Sidebar */}
        <DesktopSidebar 
          collapsed={sidebarCollapsed} 
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />

        {/* Main Content */}
        <main
          className={cn(
            "flex-1 min-h-screen min-w-0 transition-all duration-300 ease-in-out",
            // Desktop: add left margin for sidebar (w-[68px] or w-52)
            sidebarCollapsed ? "md:ml-[68px]" : "md:ml-52",
            // Mobile: add bottom padding for bottom nav
            "pb-20 md:pb-0"
          )}
        >
          <div
            className={cn(
              "mx-auto px-4 md:px-6 lg:px-8 w-full overflow-x-hidden",
              // Full width pages (like document detail, onboarding)
              isFullWidth ? "max-w-full" : "max-w-7xl"
            )}
          >
            {children}
          </div>
        </main>

        {/* Mobile Bottom Nav */}
        <MobileBottomNav />
      </div>
    </DocumentDragProvider>
  );
};

export default AppLayout;
